import loadable from "@loadable/component";
import classNames from "classnames";
import { useCallback, useMemo, useState } from "react";
import { Control, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { settings } from "@krea/common/settings";
import { usePostFieldRequest } from "@krea/common/store/field-requests/hooks/mutations";
import { ILoanApplication } from "@krea/common/types/loanApplication";
import { APPLICATION_NAME, COUNTRY_CODE } from "@krea/common/utils";
import classnames from "classnames";
import Button from "@krea/common/shared-components/button";
import GuarantorEditIcon from "@krea/common/images/svg/guarantor-edit.svg";
import CheckBox from "@krea/common/shared-components/fields/checkbox";
import FileSectionAction from "@krea/common/shared-components/fields/file-section/FileSectionAction";
import GuarantorBankingData from "./GuarantorBankingData";
import PlusSVG from "@krea/common/images/svg/plus.svg";
import { GuarantorsFormModal } from "./GuarantorsFormModal";
import { GuarantorFormSchema } from "./formHelpers";
const SVG = loadable(() => import("react-inlinesvg"));

interface IGuarantorsFormItemContactPersonProps {
  control: Control<GuarantorFormSchema>;
  contactPerson: ILoanApplication["contactPerson"] | undefined;
  fieldRequestID: number;
  guarantor?: undefined;
  guarantorFromBoardMembers?: undefined;
  isContactPerson: true;
  isDisabled?: boolean;
  submittedGuarantors: any[];
  manuallyAddGuarantor: false;
}

interface IGuarantorsFormItemBoardMemberProps {
  control: Control<GuarantorFormSchema>;
  guarantor: any;
  guarantorFromBoardMembers?: boolean;
  fieldRequestID: number;
  contactPerson?: undefined;
  isContactPerson?: false;
  isDisabled?: boolean;
  submittedGuarantors: any[];
  manuallyAddGuarantor: false;
}

interface IGuarantorsFormManuallyAddedGuarantorProps {
  control: Control<GuarantorFormSchema>;
  fieldRequestID: number;
  guarantor?: undefined;
  guarantorFromBoardMembers?: undefined;
  contactPerson?: undefined;
  isContactPerson?: false;
  isDisabled?: boolean;
  submittedGuarantors: any[];
  manuallyAddGuarantor: true;
}

type IGuarantorsFormItemProps =
  | IGuarantorsFormItemContactPersonProps
  | IGuarantorsFormItemBoardMemberProps
  | IGuarantorsFormManuallyAddedGuarantorProps;

export const GuarantorsFormItem = ({
  guarantor,
  guarantorFromBoardMembers,
  control,
  contactPerson,
  isContactPerson = false,
  fieldRequestID,
  isDisabled = false,
  submittedGuarantors,
  manuallyAddGuarantor = false,
}: IGuarantorsFormItemProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const isPartnerWeb = settings.appName === APPLICATION_NAME.PARTNER_WEB;
  const { fields, append, replace } = useFieldArray({
    control: control,
    name: "guarantors",
  });
  const { mutate: postFieldRequest } = usePostFieldRequest(fieldRequestID);
  const guarantorContactPerson = submittedGuarantors.find(
    (guarantor) => guarantor?.ssn === contactPerson?.ssn,
  );

  const openGuarantorModal = () => {
    setIsOpen(true);
  };

  const onToggleGuarantor = useCallback(
    (toggleValue: boolean) => {
      if (isContactPerson) {
        if (!contactPerson) return;

        if (toggleValue) {
          append({
            ssn: contactPerson.ssn,
            email: contactPerson.email,
          });

          postFieldRequest({
            guarantors: [
              ...fields.map((it) => ({ ssn: it.ssn, email: it.email })),
              { ssn: contactPerson.ssn, email: contactPerson.email },
            ],
          });
        } else {
          const guarantors = fields.filter(
            (it) => it.ssn !== contactPerson.ssn,
          );
          replace(guarantors);
          postFieldRequest({
            guarantors: guarantors.map((g) => ({ ssn: g.ssn, email: g.email })),
          });
        }
      } else {
        if (toggleValue) {
          append({
            ssn: guarantor?.ssn,
            email: "",
          });
          openGuarantorModal();
        } else {
          const guarantors = fields.filter((it) =>
            isFinland
              ? it.ssn.substring(0, 7) !== guarantor?.ssn.substring(0, 7)
              : it.ssn !== guarantor?.ssn,
          );
          replace(guarantors);
          postFieldRequest({
            guarantors: guarantors.map((g) => ({ ssn: g.ssn, email: g.email })),
          });
        }
      }
    },
    [
      append,
      guarantor,
      contactPerson,
      fields,
      isContactPerson,
      postFieldRequest,
      replace,
    ],
  );

  const isFinland = settings.countryCode === COUNTRY_CODE.FI;

  const [isChecked, formIndex] = useMemo(() => {
    if (isContactPerson) {
      const value = fields.find((it) => it.ssn === contactPerson?.ssn);
      return [value != null, 0] as const;
    }
    if (!manuallyAddGuarantor) {
      const index = fields.findIndex((it) =>
        isFinland
          ? it.ssn.substring(0, 7) === guarantor?.ssn.substring(0, 7)
          : it.ssn === guarantor?.ssn,
      );
      return [index !== -1, index] as const;
    } else {
      return [false, submittedGuarantors.length] as const;
    }
  }, [isContactPerson, fields, contactPerson, guarantor]);

  const label = isContactPerson
    ? `${t(
        "iaq.guarantor.me",
      )} (${contactPerson?.firstName} ${contactPerson?.lastName})`
    : manuallyAddGuarantor
      ? ""
      : guarantorFromBoardMembers
        ? guarantor.firstName + " " + guarantor.lastName
        : guarantor.first_name && guarantor.last_name
          ? guarantor.first_name + " " + guarantor.last_name
          : guarantor.ssn;

  const [action, setAction] = useState<"add" | "edit">("add");

  return (
    <>
      {!manuallyAddGuarantor ? (
        <div
          className={classNames(
            {
              "tw-border tw-border-solid tw-border-[#fbf2ff] tw-bg-[#fbf2ff] tw-rounded-sm":
                isChecked,
            },
            {
              "tw-border tw-border-solid tw-border-gray-100 tw-rounded-sm":
                !isChecked,
            },
            "tw-mb-0 tw-mt-3",
          )}
        >
          <div className="tw-flex tw-justify-between">
            <div className="tw-p-3">
              <CheckBox
                label={label}
                checked={isChecked}
                onChange={(e) => {
                  onToggleGuarantor(e.target.checked);
                  setAction("add");
                }}
                disabledCheckbox={isDisabled}
              />

              {!isPartnerWeb && guarantorContactPerson && isContactPerson && (
                <GuarantorBankingData
                  fieldRequestID={fieldRequestID}
                  ssn={guarantorContactPerson.ssn}
                  resourceId={guarantorContactPerson.id}
                />
              )}
            </div>

            {!isContactPerson && isOpen && (
              <GuarantorsFormModal
                action={action}
                fieldRequestID={fieldRequestID}
                guarantorIndex={formIndex}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                guarantorName={label}
                onToggleGuarantor={onToggleGuarantor}
                manuallyAddGuarantor={manuallyAddGuarantor}
              />
            )}

            {isChecked && !isContactPerson && (
              <FileSectionAction
                title={t("commons.edit")}
                className={"tw-mt-1 tw-mr-1"}
                iconClassName={"tw-pb-1"}
                icon={GuarantorEditIcon}
                onClick={() => {
                  openGuarantorModal();
                  setAction("edit");
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <div className={classnames("tw-flex tw-mt-4", {})}>
          <div
            className={classnames(
              "tw-border tw-border-solid tw-border-gray-200 tw-rounded-sm tw-mt-1 tw-py-1 tw-w-full",
            )}
          >
            <Button
              variant="text"
              justify="start"
              size="sm"
              className="text-left pl-4"
              startIcon={
                <div className={"tw-w-[12px]"}>
                  <SVG className="tw-block tw-w-full tw-h-auto" src={PlusSVG} />
                </div>
              }
              onClick={() => {
                setAction("add");
                openGuarantorModal();
              }}
            >
              {t("iaq.guarantor.addAnotherGuarantorOpen.empty")}
            </Button>

            {!isContactPerson && isOpen && (
              <GuarantorsFormModal
                action={action}
                fieldRequestID={fieldRequestID}
                guarantorIndex={formIndex}
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                guarantorName={label}
                onToggleGuarantor={onToggleGuarantor}
                manuallyAddGuarantor={manuallyAddGuarantor}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
